"use client"

import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  styled,
} from "@mui/material"
import { Send as SendIcon, Delete as DeleteIcon, UploadFile as UploadFileIcon } from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import { createCampaign, resetCampaignState } from "../slices/campaignSlice"
import { countries } from "../data/countries"
import { API_URL, MAX_CAMPAIGN_MESSAGE_LENGTH } from "../constants"

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

const SendCampaignPage = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth)
  const { loading, error, success, campaign } = useSelector((state) => state.campaigns)
  const { states } = useSelector((state) => state.locations)

  // Campaign config from the store
  const [campaignConfig, setCampaignConfig] = useState({
    maxImages: 2,
    maxVideos: 1,
    maxPdfs: 1,
    imageSize: "1 MB",
    videoSize: "10 MB",
    pdfSize: "1 MB",
  })
  const numbersStatsRef = useRef(null)
  // Form state
  const [campaignOption, setCampaignOption] = useState("domestic")
  const [country, setCountry] = useState("IN")
  const [countryCode, setCountryCode] = useState("+91")
  const [title, setTitle] = useState("")
  const [message, setMessage] = useState("")
  const [numbers, setNumbers] = useState("")
  const [numberFormat, setNumberFormat] = useState("manual")
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [images, setImages] = useState([])
  const [videos, setVideos] = useState([])
  const [pdfs, setPdfs] = useState([])

  // Refs for file inputs
  const imageInputRef = useRef(null)
  const videoInputRef = useRef(null)
  const pdfInputRef = useRef(null)
  const fileInputRef = useRef(null)

  // Fetch campaign config
  useEffect(() => {
    const fetchCampaignConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/campaign-config`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        })
        const data = await response.json()

        if (data && data.length > 0) {
          const configObj = {}
          data.forEach((item) => {
            if (item.name === "Number of Images") configObj.maxImages = Number.parseInt(item.value)
            if (item.name === "Number of Videos") configObj.maxVideos = Number.parseInt(item.value)
            if (item.name === "Number Of PDFs") configObj.maxPdfs = Number.parseInt(item.value)
            if (item.name === "Image Size") configObj.imageSize = item.value
            if (item.name === "Video Size") configObj.videoSize = item.value
            if (item.name === "PDF Size") configObj.pdfSize = item.value
          })
          setCampaignConfig(configObj)
        }
      } catch (error) {
        console.error("Failed to fetch campaign config:", error)
      }
    }

    fetchCampaignConfig()
  }, [userInfo.token])

  // Handle success and error states
  useEffect(() => {
    if (success) {
      toast.success("Campaign created successfully")
      // Reset form
      setTitle("")
      setMessage("")
      setNumbers("")
      setImages([])
      setVideos([])
      setPdfs([])
      dispatch(resetCampaignState())
    }
  }, [success, dispatch])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetCampaignState())
    }
  }, [error, dispatch])

  // Update country code when country changes
  useEffect(() => {
    const selectedCountry = countries.find((c) => c.code === country)
    if (selectedCountry) {
      setCountryCode(`+${selectedCountry.phone}`)
    }
  }, [country])

  const handleCampaignOptionChange = (event, newOption) => {
    if (newOption !== null) {
      setCampaignOption(newOption)
      // Reset country to India for domestic, or first non-India country for international
      if (newOption === "domestic") {
        setCountry("IN")
      } else {
        // Set to first non-India country (US)
        setCountry("US")
      }
    }
  }

  const handleCountryChange = (event) => {
    setCountry(event.target.value)
  }

  const handleNumberFormatChange = (event, newFormat) => {
    if (newFormat !== null) {
      setNumberFormat(newFormat)
      // Clear numbers when switching to CSV or Excel
      if (newFormat !== "manual") {
        setNumbers("")
      }
    }
  }

  const handleFileUpload = async (file, type) => {
    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        const fileObj = {
          name: file.name,
          url: data.filePath,
          type,
        }

        if (type === "image") {
          setImages((prev) => [...prev, fileObj])
        } else if (type === "video") {
          setVideos((prev) => [...prev, fileObj])
        } else if (type === "pdf") {
          setPdfs((prev) => [...prev, fileObj])
        }

        toast.success(`${type} uploaded successfully`)
      } else {
        throw new Error(data.message || "Failed to upload file")
      }
    } catch (error) {
      toast.error(`Failed to upload ${type}: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (images.length >= campaignConfig.maxImages) {
      toast.error(`You can only upload ${campaignConfig.maxImages} images`)
      return
    }

    handleFileUpload(file, "image")
  }

  const handleVideoUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (videos.length >= campaignConfig.maxVideos) {
      toast.error(`You can only upload ${campaignConfig.maxVideos} videos`)
      return
    }

    handleFileUpload(file, "video")
  }

  const handlePdfUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (pdfs.length >= campaignConfig.maxPdfs) {
      toast.error(`You can only upload ${campaignConfig.maxPdfs} PDFs`)
      return
    }

    handleFileUpload(file, "pdf")
  }

  const handleRemoveFile = (index, type) => {
    if (type === "image") {
      setImages((prev) => prev.filter((_, i) => i !== index))
    } else if (type === "video") {
      setVideos((prev) => prev.filter((_, i) => i !== index))
    } else if (type === "pdf") {
      setPdfs((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    setSelectedFile(file)

    if (file) {
      // Process the file content here
      const reader = new FileReader()
      reader.onload = (e) => {
        const fileContent = e.target.result
        // Handle CSV or Excel content
        setNumbers(fileContent)
      }
      reader.onerror = (e) => {
        toast.error("Error reading file")
      }

      reader.readAsText(file)
    }
  }

  // Helper functions for number processing
  const parseNumbers = (numbersStr) => {
    // Split by new lines, trim, remove empty
    return numbersStr
      .split(/\r?\n/)
      .map((n) => n.trim())
      .filter((n) => n.length > 0)
  }

  const isValidNumber = (num, countryCode) => {
    // Basic validation: digits only, length check (India: 10 digits, US: 10 digits, etc.)
    // You can expand this logic for more countries if needed
    const digitsOnly = num.replace(/\D/g, "")
    if (countryCode === "+91") {
      return /^\d{10}$/.test(digitsOnly)
    }
    if (countryCode === "+1") {
      return /^\d{10}$/.test(digitsOnly)
    }
    // Default: at least 7 digits
    return /^\d{7,}$/.test(digitsOnly)
  }

  // Memoized stats for performance
  const numberStats = (() => {
    const arr = parseNumbers(numbers)
    const seen = new Set()
    const duplicates = new Set()
    const invalids = []
    const valids = []

    arr.forEach((num) => {
      if (seen.has(num)) {
        duplicates.add(num)
      } else {
        seen.add(num)
      }
      if (isValidNumber(num, countryCode)) {
        valids.push(num)
      } else {
        invalids.push(num)
      }
    })

    return {
      total: arr.length,
      valid: valids.length,
      invalid: invalids.length,
      duplicate: duplicates.size,
      duplicates: Array.from(duplicates),
      invalidNumbers: invalids,
      validNumbers: valids,
      allNumbers: arr,
    }
  })()

  // Remove duplicates handler
  const handleRemoveDuplicates = () => {
    const uniqueNumbers = numberStats.allNumbers.filter(
      (num, idx, arr) => arr.indexOf(num) === idx
    )
    setNumbers(uniqueNumbers.join("\n"))
    toast.success("Duplicate numbers removed")
  }

  // Remove invalid numbers handler
  const handleRemoveInvalids = () => {
    setNumbers(numberStats.validNumbers.join("\n"))
    toast.success("Invalid numbers removed")
  }

  const handleSubmit = () => {
    if (numberStats.invalid > 0 || numberStats.duplicate) {
      toast.error("Please remove all invalid or duplicate numbers before submitting");
      numbersStatsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
      return
    }

    if (!title.trim()) {
      toast.error("Please enter a campaign title")
      return
    }

    if (!message.trim()) {
      toast.error("Please enter a message")
      return
    }

    if (message.length > MAX_CAMPAIGN_MESSAGE_LENGTH) {
      toast.error(`Campaign message cannot exceed ${MAX_CAMPAIGN_MESSAGE_LENGTH} characters`)
      return
    }

    if (!numbers.trim()) {
      toast.error("Please enter at least one phone number")
      return
    }

    if (numberStats.invalid > 0) {
      toast.error("Please remove all invalid numbers before submitting")
      return
    }

    if (numberStats.duplicate > 0) {
      toast.error("Please remove all duplicate numbers before submitting")
      return
    }

    const campaignData = {
      title,
      message,
      campaignType: "normal",
      campaignOption,
      numbers: numbers.trim(),
      country: countries.find((c) => c.code === country)?.label || "India",
      countryCode,
      media: [
        ...images.map((img) => ({ type: "image", url: img.url, name: img.name })),
        ...videos.map((vid) => ({ type: "video", url: vid.url, name: vid.name })),
        ...pdfs.map((pdf) => ({ type: "pdf", url: pdf.url, name: pdf.name })),
      ],
    }

    dispatch(createCampaign(campaignData))
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Send Campaign
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Campaign
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }} ref={numbersStatsRef}>
                <Chip label={`${numberStats.total} Total`} color="default" />
                <Chip label={`${numberStats.valid} Valid`} color="success" />
                <Chip label={`${numberStats.invalid} Invalid`} color="error" />
                <Chip label={`${numberStats.duplicate} Duplicate`} color="warning" />
                {numberStats.duplicate > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ ml: 1 }}
                    onClick={handleRemoveDuplicates}
                  >
                    Remove Duplicates
                  </Button>
                )}
                {numberStats.invalid > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{ ml: 1 }}
                    onClick={handleRemoveInvalids}
                  >
                    Remove Invalid
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Choose Option
              </Typography>
              <ToggleButtonGroup
                value={campaignOption}
                exclusive
                onChange={handleCampaignOptionChange}
                aria-label="campaign option"
                sx={{ mb: 3 }}
              >
                <StyledToggleButton value="domestic" aria-label="domestic">
                  Domestic
                </StyledToggleButton>
                <StyledToggleButton value="international" aria-label="international">
                  International
                </StyledToggleButton>
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Choose Country
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={country}
                  onChange={handleCountryChange}
                  displayEmpty
                  disabled={campaignOption === "domestic"}
                  renderValue={(selected) => {
                    const selectedCountry = countries.find((c) => c.code === selected)
                    return (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                          alt=""
                          style={{ marginRight: 8 }}
                        />
                        {'+'}{selectedCountry?.phone}{'  '}
                        {selectedCountry?.label}
                      </Box>
                    )
                  }}
                >
                  {countries
                    .filter((c) => campaignOption === "domestic" || c.code !== "IN")
                    .map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <img
                            loading="lazy"
                            width="20"
                            src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                            srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                            alt=""
                            style={{ marginRight: 8 }}
                          />
                          {country.label}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Campaign Title
              </Typography>
              <TextField
                fullWidth
                placeholder="Campaign Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Type your message here"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: MAX_CAMPAIGN_MESSAGE_LENGTH }}
                required
              />
              <Typography variant="caption" color="text.secondary">
                Message Character Count: {message.length}/{MAX_CAMPAIGN_MESSAGE_LENGTH}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Choose Number Format
              </Typography>
              <ToggleButtonGroup
                value={numberFormat}
                exclusive
                onChange={handleNumberFormatChange}
                aria-label="number format"
                sx={{ mb: 3 }}
              >
                <StyledToggleButton value="manual" aria-label="manual">
                  Manual
                </StyledToggleButton>
                <StyledToggleButton value="csv" aria-label="csv">
                  CSV Numbers
                </StyledToggleButton>
                <StyledToggleButton value="excel" aria-label="excel">
                  Excel Numbers
                </StyledToggleButton>
              </ToggleButtonGroup>

              {numberFormat !== "manual" && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 2 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{ mb: 1, bgcolor: "#3f51b5", color: "white", "&:hover": { bgcolor: "#303f9f" } }}
                  >
                    Choose {numberFormat === "csv" ? "CSV" : "Excel"} File
                    <input
                      type="file"
                      hidden
                      accept={numberFormat === "csv" ? ".csv" : ".xlsx, .xls"}
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Note: Only {numberFormat === "csv" ? ".csv" : ".xlsx"} is acceptable.
                    <br />
                    Note: There should be only one column and the title of the column should be "Numbers".
                    <br />
                    Note: Any other format will not be accepted.
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex" }}>
                    {numberFormat === "csv" ? (
                      <Button
                        href="/sample.csv"
                        download="sample.csv"
                        variant="text"
                        sx={{ textTransform: "none", color: "primary.main" }}
                      >
                        Download Sample CSV
                      </Button>
                    ) : (
                      <Button
                        href="/sample.xlsx"
                        download="sample.xlsx"
                        variant="text"
                        sx={{ textTransform: "none", color: "primary.main" }}
                      >
                        Download Sample Excel
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            {numberFormat === "manual" && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Mobile Numbers
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  placeholder="Enter phone numbers, one per line"
                  value={numbers}
                  onChange={(e) => setNumbers(e.target.value)}
                  onBlur={handleRemoveDuplicates}
                  required
                />
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Note: Each number should be in a new line.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>
                Max {campaignConfig.maxImages} Images
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                ref={imageInputRef}
                style={{ display: "none" }}
                disabled={isUploading || images.length >= campaignConfig.maxImages}
              />
              <Button
                variant="outlined"
                onClick={() => imageInputRef.current.click()}
                disabled={isUploading || images.length >= campaignConfig.maxImages}
                fullWidth
              >
                Select Image(Max {campaignConfig.imageSize})
              </Button>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SR. NO.</TableCell>
                      <TableCell>NAME</TableCell>
                      <TableCell>ACTION</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {images.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No images uploaded
                        </TableCell>
                      </TableRow>
                    ) : (
                      images.map((image, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{image.name}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveFile(index, "image")}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>
                Max {campaignConfig.maxVideos} Videos
              </Typography>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                ref={videoInputRef}
                style={{ display: "none" }}
                disabled={isUploading || videos.length >= campaignConfig.maxVideos}
              />
              <Button
                variant="outlined"
                onClick={() => videoInputRef.current.click()}
                disabled={isUploading || videos.length >= campaignConfig.maxVideos}
                fullWidth
              >
                Select Video(Max {campaignConfig.videoSize})
              </Button>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SR. NO.</TableCell>
                      <TableCell>NAME</TableCell>
                      <TableCell>ACTION</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {videos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No videos uploaded
                        </TableCell>
                      </TableRow>
                    ) : (
                      videos.map((video, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{video.name}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveFile(index, "video")}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>
                Max {campaignConfig.maxPdfs} PDFs
              </Typography>
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                ref={pdfInputRef}
                style={{ display: "none" }}
                disabled={isUploading || pdfs.length >= campaignConfig.maxPdfs}
              />
              <Button
                variant="outlined"
                onClick={() => pdfInputRef.current.click()}
                disabled={isUploading || pdfs.length >= campaignConfig.maxPdfs}
                fullWidth
              >
                Select PDF(Max {campaignConfig.pdfSize})
              </Button>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SR. NO.</TableCell>
                      <TableCell>NAME</TableCell>
                      <TableCell>ACTION</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pdfs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No PDFs uploaded
                        </TableCell>
                      </TableRow>
                    ) : (
                      pdfs.map((pdf, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{pdf.name}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveFile(index, "pdf")}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={loading || isUploading}
                sx={{
                  bgcolor: "#008069",
                  "&:hover": { bgcolor: "#128C7E" },
                }}
              >
                {loading ? "Sending..." : "Send Now"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </DashboardLayout>
  )
}

export default SendCampaignPage
